import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Grid,
  Alert,
} from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { MOCK_ORDER, getOrderTotals } from "./constants";
import { useThemeColor } from "../hooks/useThemeColor";
import {
  getStorePaymentConfig,
  createPaymentIntent,
  updateOrderStatus,
} from "./payAtTableApi";

const PAYMENT_PENDING_ORDER_KEY = "payAtTable_pending_order_id";

/**
 * Inner form that uses Stripe hooks. Must be rendered inside <Elements>.
 */
function CheckoutForm({ orderId, endpoint, clientSecret, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const { themeColor } = useThemeColor();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const primaryColor = themeColor || "#162A5C";

  const returnUrl = useMemo(
    () => `${window.location.origin}${window.location.pathname}`,
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !orderId || !endpoint) return;
    setErrorMessage(null);
    setLoading(true);
    try {
      // Persist orderId and endpoint for 3DS return handling
      sessionStorage.setItem(PAYMENT_PENDING_ORDER_KEY, String(orderId));
      sessionStorage.setItem("payAtTable_pending_endpoint", String(endpoint));
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          receipt_email: undefined, // optional
        },
      });
      if (error) {
        setErrorMessage(error.message || "Payment failed");
        onError?.(error);
        return;
      }
      // If we get here without redirect, payment succeeded inline (no 3DS)
      await updateOrderStatus(endpoint, orderId, {
        payment_type: "card",
        order_status: 1,
        payment_status: 1,
      });
      sessionStorage.removeItem(PAYMENT_PENDING_ORDER_KEY);
      sessionStorage.removeItem("payAtTable_pending_endpoint");
      onSuccess();
    } catch (err) {
      const msg = err?.message || "Payment failed";
      setErrorMessage(msg);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          layout: "tabs",
          defaultCollapsed: false,
          radios: true,
          spacedAccordionItems: false,
        }}
      />
      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <Button
        fullWidth
        variant="contained"
        size="large"
        type="submit"
        disabled={!stripe || loading}
        sx={{
          mt: 2,
          py: 1.5,
          bgcolor: primaryColor,
          color: "#fff",
          "&:hover": { bgcolor: primaryColor, filter: "brightness(1.1)" },
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Pay now"}
      </Button>
    </form>
  );
}

export default function StripePayment({ order, orderId, endpoint, storeId, onPay, onError }) {
  const { themeColor } = useThemeColor();
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const displayOrder = order ?? MOCK_ORDER;
  const { subtotal, tax, total } = getOrderTotals(displayOrder);
  const primaryColor = themeColor || "#162A5C";
  const canInit = endpoint && storeId && orderId;

  useEffect(() => {
    if (!canInit) return;
    let cancelled = false;
    setLoadError(null);
    (async () => {
      try {
        const publishableKey = await getStorePaymentConfig(endpoint, storeId);
        if (cancelled) return;
        setStripePromise(loadStripe(publishableKey));
        const secret = await createPaymentIntent(endpoint, orderId, false);
        if (cancelled) return;
        setClientSecret(secret);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || "Failed to load payment");
          onError?.(err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [endpoint, storeId, orderId, onError, canInit]);

  const handleSuccess = () => {
    onPay?.();
  };

  const options = useMemo(
    () => ({
      clientSecret,
      appearance: {
        theme: "stripe",
        variables: { colorPrimary: primaryColor },
      },
    }),
    [clientSecret, primaryColor]
  );

  if (!canInit) {
    return (
      <Container maxWidth="md" className="pay-at-table-step" sx={{ py: 1 }}>
        <Alert severity="warning">
          Missing store or order. Cannot load payment.
        </Alert>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="md" className="pay-at-table-step" sx={{ py: 1 }}>
        <Alert severity="error">{loadError}</Alert>
      </Container>
    );
  }

  if (!stripePromise || !clientSecret) {
    return (
      <Container maxWidth="md" className="pay-at-table-step" sx={{ py: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress sx={{ color: primaryColor }} />
        </Box>
      </Container>
    );
  }



  return (
    <Container maxWidth="md" className="pay-at-table-step" sx={{ py: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper
            elevation={2}
            sx={{
              p: 2.25,
              borderRadius: 2,
              height: "100%",
              border: "1px dashed",
              borderColor: "grey.300",
            }}
            className="pay-at-table-card"
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{
                borderBottom: "1px dashed",
                borderColor: "grey.300",
                pb: 1,
                mb: 2,
                letterSpacing: 1.4,
              }}
            >
              ORDER SUMMARY
            </Typography>
            {(displayOrder.items || []).map((item, idx) => (
              <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                <Typography variant="body2">•</Typography>
                <Typography variant="body2">
                  {item.qty}x {item.name}
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={{ ml: "auto" }}>
                  {displayOrder.currency}
                  {(item.qty * item.price).toFixed(2)}
                </Typography>
              </Box>
            ))}
            <Box sx={{ borderTop: "1px dashed", borderColor: "grey.300", mt: 1, pt: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">
                  {displayOrder.currency}
                  {subtotal.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                <Typography variant="body2">Tax</Typography>
                <Typography variant="body2">
                  {displayOrder.currency}
                  {tax.toFixed(2)}
                </Typography>
              </Box>
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
              Total: {displayOrder.currency}
              {total.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "grey.300",
            }}
            className="pay-at-table-card"
          >
            <Typography
              variant="overline"
              sx={{ letterSpacing: 1.2 }}
              color="text.secondary"
            >
              Step 4 · Payment
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, mb: 0.5 }}>
              Card details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your card details to complete the payment securely.
            </Typography>
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm
                orderId={orderId}
                endpoint={endpoint}
                clientSecret={clientSecret}
                onSuccess={handleSuccess}
                onError={onError}
              />
            </Elements>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: "center", display: "block", mt: 1 }}
            >
              Secured by Stripe
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
